import type { ClinicianAccount } from '@api'
import { ArrowLeftIcon } from '@blueprinthq/joy'
import {
  Box,
  Button,
  Divider,
  Flex,
  HStack,
  IconButton,
  Stack,
  Text,
  useBreakpointValue,
  useToast
} from '@chakra-ui/react'
import SuggestionStrength from '@handlers/sessions/components/assist-panel/suggestion-strength'
import { useExperienceManager } from '@hooks'
import React from 'react'
import { useQueryClient } from 'react-query'
import {
  getSessionControllerGetSuggestedContentQueryKey,
  useSessionControllerPostContentSuggestions
} from '~/clinician-api'

interface OverlayProps {
  client: any
  toggleAssistOverlay: () => void
  account?: ClinicianAccount | null
  session: any
}

const Overlay = ({
  client,
  toggleAssistOverlay,
  account,
  session
}: OverlayProps) => {
  const queryClient = useQueryClient()
  const toast = useToast()
  const suggestionStrengthHeight = useBreakpointValue({
    base: 'calc(100vh - 296px)',
    sm: 'calc(100vh - 296px)',
    md: 'calc(100vh - 236px)'
  })

  const { isOneClickFinalizeEnabled } = useExperienceManager()

  const handleBackClick = () => {
    toggleAssistOverlay()
  }

  const {
    mutate: postContentSuggestions,
    isLoading: isLoadingPostContentSuggestions
  } = useSessionControllerPostContentSuggestions({
    mutation: {
      onSuccess: () => {
        const suggestionListKey =
          getSessionControllerGetSuggestedContentQueryKey(session.id)
        queryClient.invalidateQueries({ queryKey: suggestionListKey })
        queryClient.setQueryData(
          suggestionListKey,
          (oldData: Record<string, unknown> | undefined) => {
            if (oldData) {
              return {
                ...oldData,
                isGenerationComplete: false
              }
            }
            return {} as Record<string, unknown>
          }
        )
        toggleAssistOverlay()
      },
      onError: (error: Error) => {
        toast({
          title: 'Error',
          description: (
            <Text whiteSpace="pre-wrap">
              {error.message || 'Failed to generate suggestions'}
            </Text>
          ),
          status: 'error',
          duration: 5000,
          isClosable: true
        })
      }
    }
  })

  return (
    <Stack spacing="0">
      <HStack spacing="0" py="9px" px="16px" h="59px">
        <IconButton
          _hover={{ bg: 'pale_gray' }}
          m="0"
          aria-label="arrow-left"
          variant="ghost"
          onClick={handleBackClick}
          icon={<ArrowLeftIcon />}
        />
        <Text textAlign="center" flex="1">
          Assist for {client.first_name}
        </Text>
        <Flex w="40px" />
      </HStack>
      <Divider />
      <Box h={suggestionStrengthHeight} overflowY="scroll">
        <SuggestionStrength account={account} session={session} />
      </Box>
      <Box p="24px">
        <Button
          colorScheme="primary"
          variant="solid"
          type="submit"
          isFullWidth
          size="lg"
          m={0}
          isLoading={isLoadingPostContentSuggestions}
          onClick={() => {
            postContentSuggestions({
              id: session.id
            })
          }}
        >
          Generate suggestions
        </Button>
      </Box>
    </Stack>
  )
}

export default Overlay
